import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Check, Minus, ShieldCheck, CalendarCheck, BookOpenCheck } from "lucide-react";
import { useApi, fileUrl } from "@/lib/api";
import { useSite } from "@/lib/site";

const Logo = ({ s }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="h-9 w-9 shrink-0 rounded-lg bg-white border border-[#DCE5E8] grid place-items-center overflow-hidden">
      {s.logo_url && !broken
        ? <img src={fileUrl(s.logo_url)} alt="" className="h-5 w-5 object-contain" onError={() => setBroken(true)} />
        : <span className="font-display text-sm font-bold text-[#17243A]">{s.name[0]}</span>}
    </span>
  );
};

const Value = ({ v }) => {
  const t = (v || "").trim();
  if (/^yes\b/i.test(t)) return <span className="flex items-start gap-1.5 text-[#17243A]"><Check className="h-3.5 w-3.5 mt-0.5 text-[#0F766E] shrink-0" />{t}</span>;
  if (/^no\b/i.test(t)) return <span className="flex items-start gap-1.5 text-[#526174]"><Minus className="h-3.5 w-3.5 mt-0.5 text-[#8A96A8] shrink-0" />{t}</span>;
  return <span className="text-[#17243A]">{t || "—"}</span>;
};

function HeroComparison() {
  const { data, loading } = useApi("/comparisons?limit=1");
  const c = data?.[0];
  if (!loading && (!c || c.software_docs.length < 2)) return null;
  const sw = c?.software_docs.slice(0, 2) || [];
  const rows = (c?.feature_rows || []).filter((r) => r.feature.toLowerCase() !== "primary focus").slice(0, 4);
  return (
    <div className="relative rise" style={{ animationDelay: "120ms" }} data-testid="hero-comparison">
      <div className="absolute -inset-2 sm:-inset-6 rounded-[2rem] bg-white/40 border border-white/70 backdrop-blur-sm" aria-hidden="true" />
      <div className="relative bg-white rounded-2xl border border-[#DCE5E8] overflow-hidden" style={{ boxShadow: "var(--shadow-lg)" }}>
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[#E7EDEF] bg-[#FAFBFC]">
          <div className="flex items-center gap-2 min-w-0">
            <span className="h-2 w-2 rounded-full bg-[#0F766E]" aria-hidden="true" />
            <span className="text-xs font-semibold text-[#17243A] truncate">Side-by-side comparison</span>
          </div>
          <span className="text-[11px] text-[#64728A] font-medium shrink-0">Editorial · factual basics</span>
        </div>
        {loading ? (
          <div className="p-5 space-y-3" aria-label="Loading comparison">
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="skeleton h-8" />)}
          </div>
        ) : (
          <div className="p-5">
            <table className="w-full text-xs table-fixed">
              <caption className="sr-only">{c.title}</caption>
              <thead>
                <tr>
                  <th scope="col" className="w-[30%] text-left pb-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A96A8]">Feature</th>
                  {sw.map((s) => (
                    <th key={s.slug} scope="col" className="text-left pb-3 px-2">
                      <span className="flex items-center gap-2"><Logo s={s} /><span className="font-display text-[13px] font-bold text-[#17243A] leading-tight">{s.name}</span></span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.feature} className="border-t border-[#EEF2F4] align-top">
                    <th scope="row" className="text-left py-3 pr-2 font-medium text-[#526174]">{r.feature}</th>
                    {sw.map((s) => <td key={s.slug} className="py-3 px-2 leading-snug"><Value v={r.values[c.software.indexOf(s.slug)]} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl bg-[#F3F7F7] border border-[#DCE9E7] px-4 py-3">
              <span className="flex items-center gap-2 text-xs text-[#17243A]"><CalendarCheck className="h-4 w-4 text-[#0F766E]" />Confirm current pricing on official sites</span>
              <Link to={`/comparisons/${c.slug}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F766E] hover:text-[#17243A] transition-colors" data-testid="hero-comparison-link">
                Read full comparison <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Geometry = () => (
  <svg aria-hidden="true" className="absolute right-[-12rem] top-[-10rem] w-[46rem] h-[46rem] text-[#3F5E7A] opacity-[0.10] pointer-events-none hidden md:block" viewBox="0 0 400 400" fill="none">
    {[60, 100, 140, 180].map((r) => <circle key={r} cx="200" cy="200" r={r} stroke="currentColor" strokeWidth="0.6" />)}
    <path d="M20 200h360M200 20v360" stroke="currentColor" strokeWidth="0.4" strokeDasharray="2 6" />
  </svg>
);

const TRUST = [[BookOpenCheck, "Independent research"], [CalendarCheck, "Dated pricing checks"], [ShieldCheck, "Affiliate links disclosed"]];

export default function HomeHero() {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const { categories } = useSite();
  return (
    <section data-testid="hero-section" className="relative hero-bg border-b border-[#DCE5E8] overflow-hidden">
      <div className="absolute inset-0 dot-texture" aria-hidden="true" />
      <Geometry />
      <div className="relative container-x pt-14 pb-20 md:pt-20 md:pb-28 grid lg:grid-cols-[1.05fr_1fr] gap-14 lg:gap-16 items-center">
        <div className="rise max-w-2xl">
          <p className="eyebrow"><span className="h-px w-6 bg-[#0F766E]" aria-hidden="true" />Discover Smarter. Choose Better.</p>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.04]">
            Find the Right Software <span className="text-[#3F5E7A]">for Your Business<span className="-ml-[0.06em]">.</span></span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#526174] max-w-xl leading-relaxed">
            Discover, compare, and evaluate business software with practical reviews, detailed comparisons, and expert-informed buying guides.
          </p>
          <form role="search" onSubmit={(e) => { e.preventDefault(); nav(`/software${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`); }}
            className="mt-9 flex items-center gap-2 rounded-2xl bg-white border border-[#CBD6DB] p-2 pl-4 sm:pl-5 focus-within:border-[#0F766E] focus-within:ring-4 focus-within:ring-[#0F766E]/10 transition-[box-shadow,border-color] max-w-xl"
            style={{ boxShadow: "var(--shadow-md)" }}>
            <Search className="h-5 w-5 text-[#8A96A8] shrink-0" aria-hidden="true" />
            <label htmlFor="hero-q" className="sr-only">Search software</label>
            <input id="hero-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search CRM, payroll…"
              className="flex-1 min-w-0 bg-transparent text-[15px] text-[#17243A] placeholder:text-[#8A96A8] outline-none py-2.5" data-testid="hero-search-input" />
            <button className="btn-primary !rounded-xl !px-4 sm:!px-6 !py-3" data-testid="hero-search-button">Search</button>
          </form>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-[#64728A] mr-1">Popular:</span>
            {categories.slice(0, 4).map((c) => (
              <Link key={c.slug} to={`/categories/${c.slug}`} data-testid={`hero-quick-${c.slug}`}
                className="text-xs font-medium text-[#3F5E7A] bg-white/80 border border-[#DCE5E8] rounded-full px-3 py-1 hover:border-[#0F766E] hover:text-[#0F766E] transition-colors">{c.name}</Link>
            ))}
          </div>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/software" className="btn-primary !px-6 !py-3" data-testid="hero-explore-button">Explore Software <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/comparisons" className="btn-secondary !px-6 !py-3" data-testid="hero-compare-button">Compare Solutions</Link>
          </div>
          <ul className="mt-10 pt-6 border-t border-[#DCE5E8] flex flex-wrap gap-x-6 gap-y-3">
            {TRUST.map(([Icon, t]) => <li key={t} className="flex items-center gap-2 text-xs font-medium text-[#526174]"><Icon className="h-4 w-4 text-[#0F766E]" />{t}</li>)}
          </ul>
        </div>
        <HeroComparison />
      </div>
    </section>
  );
}
